// ===== DATA LAYER =====
//
// Bridges the API's submission shape to the shape the gallery UI renders.
//
//   API item:  { id, guest_name, guest_message, photo_url, audio_url, created_at }
//   Gallery:   { id, guestName, timestamp, caption, photoUrl, audioUrl, createdAt }
//
// Only APPROVED submissions are ever returned by the API, and the token used is
// scoped to a single event — so the gallery can only ever show the correct
// couple's approved entries, never another event's data.

import { fetchSubmissionsPage } from './api.js';

/**
 * Map one API submission to the gallery's internal shape.
 * `audio_url` is always present; `photo_url` may be null (audio-only card).
 * Use the URLs as-is — they may be time-limited signed URLs.
 */
function mapSubmission(item) {
    return {
        id: item.id,
        guestName: item.guest_name || 'Guest',
        caption: item.guest_message || '',
        photoUrl: item.photo_url || null,
        audioUrl: item.audio_url || null,
        createdAt: item.created_at || null,
        timestamp: formatTimestamp(item.created_at),
        // The real duration is read from the <audio> element once metadata
        // loads; this is just a placeholder until then.
        duration: 0,
    };
}

// Format an ISO timestamp into a short, friendly local time like "2:14 PM".
function formatTimestamp(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Load ALL approved submissions for an event by walking the paginated API
 * (20 per page) until `meta.last_page` is reached.
 *
 * @param {string} slug
 * @param {string} token  event-scoped bearer token
 * @returns {Promise<{ event: { coupleName, weddingDate, couplePhotoUrl, greetingAudioUrl }|null, submissions: Array }>}
 * @throws {ApiError} bubbles up (notably 401) so the caller can return to the PIN gate
 */
export async function loadGallery(slug, token) {
    const first = await fetchSubmissionsPage(slug, token, 1);

    const all = [...first.data];
    const lastPage = Number(first.meta?.last_page) || 1;

    for (let page = 2; page <= lastPage; page++) {
        const next = await fetchSubmissionsPage(slug, token, page);
        all.push(...next.data);
    }

    return {
        event: first.event
            ? {
                  coupleName: first.event.couple_name || '',
                  weddingDate: first.event.wedding_date || '',
                  // The couple's intro assets. Ready-to-use URLs (may be signed
                  // / time-limited); use as-is. Either can be null, in which
                  // case the intro falls back to the bundled defaults.
                  couplePhotoUrl: first.event.couple_photo_url || null,
                  greetingAudioUrl: first.event.greeting_audio_url || null,
              }
            : null,
        submissions: all.map(mapSubmission),
    };
}
